import React from 'react';
import {Button, FlexRow, SplitButton, Tooltip} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {useReportBuilder} from '../ReportBuilderContext';

/** Top bar shown in view mode. Editors additionally see "Share" and "Edit Report" (switches
 * `mode` to `'edit'`); readers only ever see "Download". Download/Share are disabled stubs (with
 * a tooltip explaining why) — real behaviour lands in a later ticket. Left-to-right order is
 * Download, Share, Edit Report, matching the design — the reverse of
 * DOM/visual emphasis order used previously. Download uses the same `SplitButton`-with-caret
 * pattern as EditTopBar — every screenshot shows the dropdown caret in both modes. */
export function ViewTopBar(): JSX.Element {
	const {formatMessage} = useIntl();
	const {role, setMode} = useReportBuilder();

	const stubTooltip = formatMessage({id: 'ai_report_builder.stub_action_tooltip'});

	return (
		<FlexRow justifyContent={'flex-end'} alignItems={'center'} gap={'s'} data-cy={'ai-report-view-top-bar'}>
			<Tooltip content={stubTooltip}>
				<span>
					{/* SplitButton.Section only renders its trailing dropdown caret when
					 * `dropdownMenuOptions` is non-empty (see SplitButton.js), so the caret section
					 * carries a single disabled stub option — the whole control is disabled visually
					 * via the tooltip-wrapped `disabled` prop on both sections; real download-format
					 * behaviour lands in a later ticket. See EditTopBar for the same pattern/rationale. */}
					<SplitButton>
						<SplitButton.Section
							emphasis={'medium'}
							icon={'download'}
							disabled
							text={formatMessage({id: 'ai_report_builder.download'})}
							aria-label={formatMessage({id: 'ai_report_builder.download'})}
						/>
						<SplitButton.Section
							emphasis={'medium'}
							icon={'chevronDown'}
							disabled
							iconOnly
							aria-label={formatMessage({id: 'ai_report_builder.download_options'})}
							dropdownMenuOptions={[
								{
									name: formatMessage({id: 'ai_report_builder.download'}),
									onClick: () => {
										/* disabled option */
									},
									disabled: true,
								},
							]}
						/>
					</SplitButton>
				</span>
			</Tooltip>
			{role === 'editor' ? (
				<Tooltip content={stubTooltip}>
					<span>
						<Button
							emphasis={'medium'}
							icon={'share'}
							disabled
							data-cy={'ai-report-share-button'}
							aria-label={formatMessage({id: 'ai_report_builder.share'})}
						>
							{formatMessage({id: 'ai_report_builder.share'})}
						</Button>
					</span>
				</Tooltip>
			) : null}
			{role === 'editor' ? (
				<Button
					emphasis={'high'}
					icon={'edit'}
					onPress={() => setMode('edit')}
					data-cy={'ai-report-edit-report-button'}
				>
					{formatMessage({id: 'ai_report_builder.edit_report'})}
				</Button>
			) : null}
		</FlexRow>
	);
}
